import { aiCallClosureDecisionHeadlineFr } from "@/lib/litigeDecisionLabels";

function extractSynthèseIaFromLitigeMessage(raw) {
  const s = String(raw || "");
  const idx = s.search(/\bSynthèse IA\s*:/i);
  if (idx < 0) return "";
  let tail = s.slice(idx).replace(/^[\s\S]*?\bSynthèse IA\s*:\s*/i, "").trim();
  const stop = tail.search(/\n\n(?:Mon compte rendu|Je demande)/i);
  if (stop > 0) tail = tail.slice(0, stop).trim();
  return tail;
}

function IaIndicator({ label, value, tone = "neutral" }) {
  const toneClass =
    tone === "yes"
      ? "border-emerald-200 bg-emerald-50 text-emerald-900"
      : tone === "alert"
        ? "border-amber-200 bg-amber-50 text-amber-900"
        : "border-slate-200 bg-slate-50 text-slate-800";
  return (
    <div className={`min-w-0 rounded-lg border px-2.5 py-2 ${toneClass}`}>
      <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{label}</p>
      <p className="mt-0.5 break-words text-sm font-semibold tabular-nums">{value}</p>
    </div>
  );
}

/** Synthèse IA clôture d’appel (`transactions.ai_analysis`). */
export default function CallAiAnalysisBlock({ ia, litigeMessageRaw = "" }) {
  if (ia == null) {
    return (
      <p className="text-sm text-slate-500">
        Aucune analyse enregistrée (appel non clôturé côté téléphonie ou synthèse indisponible).
      </p>
    );
  }
  if (typeof ia === "string") {
    const t = ia.trim();
    return t ? (
      <p className="text-sm text-slate-800 whitespace-pre-wrap leading-relaxed">{t}</p>
    ) : (
      <p className="text-sm text-slate-500">—</p>
    );
  }

  const obj = ia && typeof ia === "object" && !Array.isArray(ia) ? ia : null;
  if (!obj) {
    return <p className="text-sm text-slate-500">Synthèse indisponible (format non reconnu).</p>;
  }

  const harvest = obj.harvest_analysis && typeof obj.harvest_analysis === "object" ? obj.harvest_analysis : null;
  const refusalNested =
    obj.refusal_analysis && typeof obj.refusal_analysis === "object" ? obj.refusal_analysis : null;
  const refusal = refusalNested || (obj.refusal_analysis ? obj : null);

  const decisionHeadline = aiCallClosureDecisionHeadlineFr(obj.decision);
  const explanationRaw =
    (refusal && (refusal.reason || refusal.reasoning)) ||
    (harvest && harvest.reason) ||
    obj.reason ||
    obj.reasoning ||
    extractSynthèseIaFromLitigeMessage(litigeMessageRaw) ||
    null;
  const explanation = String(explanationRaw || "").trim();

  const confidenceSrc =
    refusal?.confidence != null ? refusal.confidence : harvest?.confidence != null ? harvest.confidence : obj.confidence;
  const confidencePct =
    confidenceSrc != null && Number.isFinite(Number(confidenceSrc))
      ? `${Math.round(Number(confidenceSrc) * 100)} %`
      : null;

  const indicators = [];
  if (refusal && typeof refusal.legitimate_refusal === "boolean") {
    indicators.push({
      key: "refusal",
      label: "Refus fondé",
      value: refusal.legitimate_refusal ? "Oui" : "Non",
      tone: refusal.legitimate_refusal ? "yes" : "neutral",
    });
  }
  if (refusal && typeof refusal.diversion_detected === "boolean") {
    indicators.push({
      key: "diversion",
      label: "Détournement",
      value: refusal.diversion_detected ? "Oui" : "Non",
      tone: refusal.diversion_detected ? "alert" : "neutral",
    });
  }
  const harvestViolation =
    harvest && typeof harvest.contact_harvest_violation === "boolean"
      ? harvest.contact_harvest_violation
      : typeof obj.contact_harvest_violation === "boolean"
        ? obj.contact_harvest_violation
        : null;
  if (harvestViolation != null) {
    indicators.push({
      key: "harvest",
      label: "Anti-contournement",
      value: harvestViolation ? "Violation" : "Conforme",
      tone: harvestViolation ? "alert" : "yes",
    });
  }
  if (confidencePct) {
    indicators.push({ key: "confidence", label: "Confiance IA", value: confidencePct, tone: "neutral" });
  }

  const platformRule =
    typeof obj.platform_rule_violation === "string" ? String(obj.platform_rule_violation).trim() : "";

  if (obj.seller_capture_failed) {
    indicators.push({
      key: "capture_failed",
      label: "Capture Stripe",
      value: "Échec — action admin",
      tone: "alert",
    });
  }

  const hasContent = decisionHeadline || explanation || indicators.length > 0 || platformRule;

  if (!hasContent) {
    return <p className="text-sm text-slate-500">Synthèse enregistrée sans détail exploitable.</p>;
  }

  return (
    <div className="min-w-0 max-w-full space-y-3">
      {decisionHeadline ? (
        <div className="rounded-lg border border-[#2D7FF9]/25 bg-[#2D7FF9]/5 px-3 py-2.5">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-[#0B3F8C]/75">
            Décision automatique (appel)
          </p>
          <p className="mt-1 text-sm font-medium leading-snug text-slate-900">{decisionHeadline}</p>
        </div>
      ) : null}
      {explanation ? (
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-1">
            Explication de l’analyse
          </p>
          <p className="text-sm text-slate-800 leading-relaxed whitespace-pre-wrap">{explanation}</p>
        </div>
      ) : decisionHeadline || indicators.length > 0 ? (
        <p className="text-sm text-slate-500 italic">
          Aucun texte détaillé enregistré pour cette analyse (seule la décision et les indicateurs sont disponibles).
        </p>
      ) : null}
      {indicators.length > 0 ? (
        <div className="grid min-w-0 grid-cols-2 gap-2 sm:grid-cols-2 lg:grid-cols-4">
          {indicators.map(({ key, label, value, tone }) => (
            <IaIndicator key={key} label={label} value={value} tone={tone} />
          ))}
        </div>
      ) : null}
      {platformRule ? (
        <p className="text-xs text-slate-600">
          <span className="font-semibold text-slate-700">Règle plateforme :</span> {platformRule}
        </p>
      ) : null}
    </div>
  );
}
